import { useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { Edit } from '@mui/icons-material';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { abyssColors } from '@/theme/colors';
import {
  TemplateFieldTypeEnum,
  ReportDetailsDocument,
  useUpdateReportDataMutation,
} from '@/__generated__/graphql';
import { Tooltip } from '@mui/material';

interface EditableFieldProps {
  allowEditing: boolean;
  fieldName: string;
  fieldKey: string;
  value: string | number;
  editableFields: string[];
  templateId: string;
  assemblyId: string;
  formatValue: (key: string, value: string | number) => string | number;
}

export interface EditableFieldRef {
  saveChanges: (newValue?: string, columnKey?: string, columName?: string) => void | Promise<void>;
}
export const EditableReportField = forwardRef<EditableFieldRef, EditableFieldProps>(
  (
    {
      allowEditing,
      fieldName,
      fieldKey,
      value,
      editableFields,
      templateId,
      assemblyId,
      formatValue,
    },
    ref
  ) => {
    const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
    const setInsightsAssemblies = useSetAtom(state.allAssembliesForInsights)
    const [componentDetailsById, setComponentDetailsById] = useAtom(state.componentDetailsById);
    const assemblyDetails = componentDetailsById?.[assemblyId ?? ''] ?? {};
    const assemblyTemplate = useAtomValue(state.assemblyTemplate);
    const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
    const [isEditing, setIsEditing] = useState(false);
    const [editedValue, setEditedValue] = useState(value?.toString());

    const canEdit = allowEditing && editableFields.includes(fieldKey);

    const [updateTemplateFieldData, { loading }] = useUpdateReportDataMutation({
      refetchQueries: [ReportDetailsDocument],
    });

    const handleEditClick = useCallback(() => {
      setIsEditing(true);
      setEditedValue(value?.toString());
    }, [value]);

    const templateField = useMemo(() => {
      return assemblyTemplate?.fields.find((field) => field.key === fieldKey);
    }, [assemblyTemplate?.fields, fieldKey]);

    const saveChanges = useCallback(
      async (newValue?: string, columnKey?: string, columnName?: string) => {
        const templateFieldName = columnName ?? fieldName
        const templateFieldKey = columnKey ?? fieldKey
        setIsEditing(false);
        const finalValue = newValue ?? editedValue;
        const id = selectedAssemblyId || assemblyId;

        if (finalValue !== assemblyDetails?.[templateFieldKey] && templateId && id) {
          updateTemplateFieldData({
            variables: {
              input: {
                assemblyId: id,
                key: templateFieldKey,
                newValue: finalValue,
                templateId,
                lastModifiedFor: `${templateFieldName} update`,
              },
            },
            onCompleted: (data) => {
              const detailsById = {
                [id]: data?.updateSingleTemplateFieldData?.templateFieldData,
              };
              setComponentDetailsById(detailsById);
              showSnackBar('The data has been updated successfully', 'success');
              setInsightsAssemblies((prevAssemblies) => {
                const index = prevAssemblies.findIndex(a => a.id === id);

                if (index === -1) return prevAssemblies;

                const updatedAssembly = {
                  ...prevAssemblies[index],
                  templateFieldData: {
                    ...prevAssemblies[index].templateFieldData,
                    ...detailsById[id],
                  },
                };

                return [
                  ...prevAssemblies.slice(0, index),
                  updatedAssembly,
                  ...prevAssemblies.slice(index + 1),
                ];
              });

            },
            onError: (error) => {
              showSnackBar(`Failed to update ${fieldName} - ${error.message}`, 'error');
            },
          });
        }
      },
      [
        editedValue,
        fieldName,
        fieldKey,
        selectedAssemblyId,
        showSnackBar,
        templateId,
        updateTemplateFieldData,
        assemblyId,
      ]
    );
    useImperativeHandle(ref, () => ({
      saveChanges,
    }));
    const handleKeyDown = useCallback(
      async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          await saveChanges();
        }
      },
      [saveChanges]
    );

    const handleBlur = useCallback(async () => {
      await saveChanges();
    }, [saveChanges]);

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          if (!isEditing) {
            handleEditClick();
          }
        }}
        sx={{ width: '100%' }}
      >
        {isEditing && canEdit ? (
          <TextField
            variant="standard"
            value={editedValue}
            onChange={(e) => {
              const newValue = e?.target?.value;
              setEditedValue(newValue);

              if (
                templateField?.type === TemplateFieldTypeEnum.SingleSelect ||
                templateField?.type === TemplateFieldTypeEnum.MultiSelect
              ) {
                saveChanges(newValue);
              }
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus
            fullWidth
            select={
              templateField?.type === TemplateFieldTypeEnum.SingleSelect ||
              templateField?.type === TemplateFieldTypeEnum.MultiSelect
            }
            slotProps={{
              input: {
                disableUnderline: true,
                sx: {
                  flex: 1,
                  fontWeight: 500,
                  fontSize: '1.2rem',
                  color: abyssColors.primary[500],
                },
              },
            }}
          >
            {(templateField?.options || []).map((option: string) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Tooltip title={formatValue(fieldKey, value)}>
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                fontWeight: 500,
                fontSize: '1.2rem',
                color: abyssColors.primary[500],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatValue(fieldKey, value)}
            </Typography>
          </Tooltip>
        )}

        {canEdit && !isEditing && (
          <IconButton size="small" onClick={handleEditClick}>
            {loading ? (
              <CircularProgress
                size={15}
                sx={{
                  color: abyssColors.primary[300],
                }}
              />
            ) : (
              <Edit
                fontSize="inherit"
                sx={{
                  cursor: 'pointer',
                  color: abyssColors.primary[300],
                }}
              />
            )}
          </IconButton>
        )}
      </Stack>
    );
  });